/**
 * Intelligent Product Categorization System
 * Analyzes product titles, URLs, and context to categorize items
 */

// Category definitions with keywords and domains
const CATEGORIES = {
  fashion: {
    name: 'Fashion & Accessories',
    keywords: [
      // Clothing
      'dress', 'shirt', 'pants', 'jeans', 'jacket', 'coat', 'sweater', 'hoodie', 'blazer', 'suit',
      'skirt', 'shorts', 'leggings', 'underwear', 'bra', 'panties', 'socks', 'tights',
      // Accessories
      'bag', 'purse', 'wallet', 'belt', 'scarf', 'hat', 'cap', 'gloves', 'sunglasses', 'jewelry',
      'necklace', 'earrings', 'bracelet', 'ring', 'watch', 'backpack', 'handbag',
      // Shoes
      'shoes', 'sneakers', 'boots', 'heels', 'sandals', 'flats', 'pumps', 'loafers',
      // Fashion terms
      'fashion', 'style', 'outfit', 'clothing', 'apparel', 'wear', 'designer',
      // Italian
      'vestito', 'camicia', 'pantaloni', 'giacca', 'cappotto', 'maglione', 'felpa', 'gonna',
      'scarpe', 'borsa', 'portafoglio', 'cintura', 'sciarpa', 'cappello', 'occhiali', 'gioielli',
      // Spanish
      'vestido', 'camisa', 'pantalones', 'chaqueta', 'abrigo', 'suéter', 'falda', 'zapatos',
      'bolso', 'cartera', 'cinturón', 'bufanda', 'sombrero', 'gafas', 'joyas',
      // French
      'robe', 'chemise', 'pantalon', 'veste', 'manteau', 'pull', 'jupe', 'chaussures',
      'sac', 'portefeuille', 'ceinture', 'écharpe', 'chapeau', 'lunettes', 'bijoux',
      // German
      'kleid', 'hemd', 'hose', 'jacke', 'mantel', 'pullover', 'rock', 'schuhe',
      'tasche', 'geldbörse', 'gürtel', 'schal', 'hut', 'brille', 'schmuck',
      // Portuguese
      'vestido', 'camisa', 'calças', 'jaqueta', 'casaco', 'suéter', 'saia', 'sapatos',
      'bolsa', 'carteira', 'cinto', 'cachecol', 'chapéu', 'óculos', 'joias'
    ],
    domains: [
      'zara.com', 'hm.com', 'uniqlo.com', 'asos.com', 'zalando', 'nike.com', 'adidas.com',
      'gucci.com', 'prada.com', 'louisvuitton.com', 'chanel.com', 'dior.com', 'versace.com',
      'forever21.com', 'gap.com', 'oldnavy.com', 'urbanoutfitters.com', 'anthropologie.com',
      'nordstrom.com', 'macys.com', 'saks.com', 'net-a-porter.com', 'ssense.com',
      'farfetch.com', 'mytheresa.com', 'matchesfashion.com', 'shopbop.com', 'revolve.com'
    ]
  },
  
  electronics: {
    name: 'Electronics & Gadgets',
    keywords: [
      // Devices
      'phone', 'smartphone', 'iphone', 'android', 'tablet', 'ipad', 'laptop', 'computer', 'pc',
      'macbook', 'imac', 'monitor', 'screen', 'display', 'keyboard', 'mouse', 'headphones',
      'earbuds', 'speaker', 'camera', 'drone', 'smartwatch', 'fitbit', 'apple watch',
      // Gaming
      'gaming', 'console', 'playstation', 'xbox', 'nintendo', 'switch', 'controller', 'gamepad',
      // Audio/Video
      'tv', 'television', 'sound', 'audio', 'video', 'bluetooth', 'wireless', 'charger',
      'cable', 'adapter', 'powerbank', 'battery',
      // Tech terms
      'tech', 'technology', 'gadget', 'device', 'electronic', 'smart', 'digital',
      // Italian
      'telefono', 'smartphone', 'tablet', 'computer', 'portatile', 'cuffie', 'altoparlante',
      'fotocamera', 'orologio', 'intelligente', 'console', 'controller', 'caricatore',
      // Spanish
      'teléfono', 'móvil', 'celular', 'tableta', 'ordenador', 'computadora', 'auriculares',
      'altavoz', 'cámara', 'reloj', 'inteligente', 'consola', 'controlador', 'cargador',
      // French
      'téléphone', 'portable', 'tablette', 'ordinateur', 'casque', 'haut-parleur',
      'appareil photo', 'montre', 'intelligent', 'console', 'contrôleur', 'chargeur',
      // German
      'telefon', 'handy', 'tablet', 'computer', 'laptop', 'kopfhörer', 'lautsprecher',
      'kamera', 'uhr', 'intelligent', 'konsole', 'controller', 'ladegerät',
      // Portuguese
      'telefone', 'celular', 'tablet', 'computador', 'laptop', 'fones', 'alto-falante',
      'câmera', 'relógio', 'inteligente', 'console', 'controle', 'carregador'
    ],
    domains: [
      'apple.com', 'samsung.com', 'google.com', 'microsoft.com', 'dell.com', 'hp.com',
      'lenovo.com', 'asus.com', 'acer.com', 'sony.com', 'lg.com', 'huawei.com',
      'amazon.com', 'bestbuy.com', 'newegg.com', 'bhphotovideo.com', 'adorama.com',
      'gamestop.com', 'nintendo.com', 'playstation.com', 'xbox.com', 'steam.com'
    ]
  },

  home_decor: {
    name: 'Home & Decor',
    keywords: [
      // Furniture
      'furniture', 'chair', 'table', 'sofa', 'couch', 'bed', 'mattress', 'desk', 'shelf',
      'cabinet', 'dresser', 'wardrobe', 'bookshelf', 'nightstand', 'ottoman', 'bench',
      // Decor
      'decor', 'decoration', 'art', 'painting', 'poster', 'frame', 'mirror', 'lamp',
      'light', 'lighting', 'candle', 'vase', 'plant', 'flower', 'pillow', 'cushion',
      'curtain', 'blind', 'rug', 'carpet', 'throw', 'blanket',
      // Kitchen & Bath
      'kitchen', 'bathroom', 'cookware', 'dinnerware', 'plates', 'cups', 'glasses',
      'cutlery', 'utensils', 'towel', 'shower', 'bath', 'toilet',
      // Home terms
      'home', 'house', 'room', 'living', 'bedroom', 'dining', 'interior', 'design',
      // Italian
      'casa', 'arredamento', 'mobile', 'sedia', 'tavolo', 'divano', 'letto', 'scrivania',
      'libreria', 'armadio', 'specchio', 'lampada', 'luce', 'candela', 'vaso', 'pianta',
      'cuscino', 'tenda', 'tappeto', 'cucina', 'bagno', 'piatti', 'bicchieri',
      // Spanish
      'casa', 'hogar', 'mueble', 'silla', 'mesa', 'sofá', 'cama', 'escritorio',
      'estante', 'armario', 'espejo', 'lámpara', 'luz', 'vela', 'jarrón', 'planta',
      'almohada', 'cortina', 'alfombra', 'cocina', 'baño', 'platos', 'vasos',
      // French
      'maison', 'meuble', 'chaise', 'table', 'canapé', 'lit', 'bureau', 'étagère',
      'armoire', 'miroir', 'lampe', 'lumière', 'bougie', 'vase', 'plante', 'coussin',
      'rideau', 'tapis', 'cuisine', 'salle de bain', 'assiettes', 'verres',
      // German
      'haus', 'möbel', 'stuhl', 'tisch', 'sofa', 'bett', 'schreibtisch', 'regal',
      'schrank', 'spiegel', 'lampe', 'licht', 'kerze', 'vase', 'pflanze', 'kissen',
      'vorhang', 'teppich', 'küche', 'badezimmer', 'teller', 'gläser',
      // Portuguese
      'casa', 'lar', 'móvel', 'cadeira', 'mesa', 'sofá', 'cama', 'escrivaninha',
      'estante', 'armário', 'espelho', 'lâmpada', 'luz', 'vela', 'vaso', 'planta',
      'almofada', 'cortina', 'tapete', 'cozinha', 'banheiro', 'pratos', 'copos'
    ],
    domains: [
      'ikea.com', 'wayfair.com', 'homedepot.com', 'lowes.com', 'target.com', 'walmart.com',
      'westelm.com', 'potterybarn.com', 'crateandbarrel.com', 'cb2.com', 'anthropologie.com',
      'urbanoutfitters.com', 'overstock.com', 'amazon.com', 'homegoods.com', 'pier1.com'
    ]
  },

  beauty: {
    name: 'Beauty & Skincare',
    keywords: [
      // Makeup
      'makeup', 'cosmetics', 'foundation', 'concealer', 'powder', 'blush', 'bronzer',
      'eyeshadow', 'eyeliner', 'mascara', 'lipstick', 'lip gloss', 'nail polish',
      // Skincare
      'skincare', 'moisturizer', 'cleanser', 'toner', 'serum', 'cream', 'lotion',
      'sunscreen', 'mask', 'scrub', 'oil', 'treatment', 'anti-aging', 'acne',
      // Hair
      'hair', 'shampoo', 'conditioner', 'styling', 'gel', 'mousse', 'spray', 'oil',
      // Fragrance
      'perfume', 'cologne', 'fragrance', 'scent', 'eau de toilette', 'eau de parfum',
      // Beauty terms
      'beauty', 'cosmetic', 'skincare', 'personal care', 'grooming',
      // Italian
      'bellezza', 'cosmetici', 'trucco', 'fondotinta', 'correttore', 'cipria', 'blush',
      'ombretto', 'eyeliner', 'mascara', 'rossetto', 'smalto', 'cura della pelle',
      'idratante', 'detergente', 'tonico', 'siero', 'crema', 'protezione solare',
      'maschera', 'scrub', 'olio', 'capelli', 'shampoo', 'balsamo', 'profumo',
      // Spanish
      'belleza', 'cosméticos', 'maquillaje', 'base', 'corrector', 'polvo', 'colorete',
      'sombra', 'delineador', 'máscara', 'pintalabios', 'esmalte', 'cuidado de la piel',
      'hidratante', 'limpiador', 'tónico', 'suero', 'crema', 'protector solar',
      'mascarilla', 'exfoliante', 'aceite', 'cabello', 'champú', 'acondicionador', 'perfume',
      // French
      'beauté', 'cosmétiques', 'maquillage', 'fond de teint', 'correcteur', 'poudre', 'blush',
      'ombre à paupières', 'eye-liner', 'mascara', 'rouge à lèvres', 'vernis', 'soin de la peau',
      'hydratant', 'nettoyant', 'tonique', 'sérum', 'crème', 'écran solaire',
      'masque', 'gommage', 'huile', 'cheveux', 'shampooing', 'après-shampooing', 'parfum',
      // German
      'schönheit', 'kosmetik', 'make-up', 'foundation', 'concealer', 'puder', 'rouge',
      'lidschatten', 'eyeliner', 'mascara', 'lippenstift', 'nagellack', 'hautpflege',
      'feuchtigkeitscreme', 'reiniger', 'toner', 'serum', 'creme', 'sonnenschutz',
      'maske', 'peeling', 'öl', 'haar', 'shampoo', 'spülung', 'parfüm',
      // Portuguese
      'beleza', 'cosméticos', 'maquiagem', 'base', 'corretivo', 'pó', 'blush',
      'sombra', 'delineador', 'máscara', 'batom', 'esmalte', 'cuidados com a pele',
      'hidratante', 'limpador', 'tônico', 'sérum', 'creme', 'protetor solar',
      'máscara', 'esfoliante', 'óleo', 'cabelo', 'xampu', 'condicionador', 'perfume'
    ],
    domains: [
      'sephora.com', 'ulta.com', 'nordstrom.com', 'macys.com', 'dermstore.com',
      'beautylish.com', 'nykaa.com', 'lookfantastic.com', 'spacenk.com', 'beautybay.com',
      'cultbeauty.co.uk', 'feelunique.com', 'douglas.de', 'marionnaud.fr'
    ]
  },

  sports: {
    name: 'Sports & Fitness',
    keywords: [
      // Sports
      'sport', 'sports', 'fitness', 'gym', 'workout', 'exercise', 'training', 'athletic',
      'running', 'jogging', 'cycling', 'swimming', 'yoga', 'pilates', 'weightlifting',
      'cardio', 'strength', 'endurance', 'marathon', 'triathlon',
      // Equipment
      'equipment', 'gear', 'weights', 'dumbbell', 'barbell', 'kettlebell', 'treadmill',
      'bike', 'bicycle', 'helmet', 'ball', 'racket', 'bat', 'gloves', 'mat',
      // Apparel
      'activewear', 'sportswear', 'athletic wear', 'leggings', 'shorts', 'tank top',
      'sports bra', 'sneakers', 'running shoes', 'cleats', 'jersey', 'tracksuit',
      // Supplements
      'protein', 'supplement', 'vitamin', 'energy', 'recovery', 'nutrition',
      // Italian
      'sport', 'fitness', 'palestra', 'allenamento', 'esercizio', 'corsa', 'ciclismo',
      'nuoto', 'yoga', 'pesi', 'manubrio', 'bicicletta', 'casco', 'palla', 'racchetta',
      'guanti', 'tappetino', 'abbigliamento sportivo', 'leggings', 'pantaloncini',
      'scarpe da corsa', 'maglietta', 'tuta', 'proteine', 'integratore',
      // Spanish
      'deporte', 'fitness', 'gimnasio', 'entrenamiento', 'ejercicio', 'carrera', 'ciclismo',
      'natación', 'yoga', 'pesas', 'mancuerna', 'bicicleta', 'casco', 'pelota', 'raqueta',
      'guantes', 'esterilla', 'ropa deportiva', 'mallas', 'pantalones cortos',
      'zapatillas', 'camiseta', 'chándal', 'proteína', 'suplemento',
      // French
      'sport', 'fitness', 'gymnastique', 'entraînement', 'exercice', 'course', 'cyclisme',
      'natation', 'yoga', 'poids', 'haltère', 'vélo', 'casque', 'balle', 'raquette',
      'gants', 'tapis', 'vêtements de sport', 'leggings', 'short',
      'chaussures de course', 'maillot', 'survêtement', 'protéine', 'supplément',
      // German
      'sport', 'fitness', 'fitnessstudio', 'training', 'übung', 'laufen', 'radfahren',
      'schwimmen', 'yoga', 'gewichte', 'hantel', 'fahrrad', 'helm', 'ball', 'schläger',
      'handschuhe', 'matte', 'sportbekleidung', 'leggings', 'shorts',
      'laufschuhe', 'trikot', 'trainingsanzug', 'protein', 'nahrungsergänzung',
      // Portuguese
      'esporte', 'fitness', 'academia', 'treino', 'exercício', 'corrida', 'ciclismo',
      'natação', 'yoga', 'pesos', 'haltere', 'bicicleta', 'capacete', 'bola', 'raquete',
      'luvas', 'tapete', 'roupas esportivas', 'legging', 'shorts',
      'tênis de corrida', 'camiseta', 'agasalho', 'proteína', 'suplemento'
    ],
    domains: [
      'nike.com', 'adidas.com', 'underarmour.com', 'reebok.com', 'puma.com', 'asics.com',
      'newbalance.com', 'lululemon.com', 'athleta.com', 'patagonia.com', 'thenorthface.com',
      'dickssportinggoods.com', 'rei.com', 'decathlon.com', 'sports-direct.com'
    ]
  },

  books: {
    name: 'Books & Education',
    keywords: [
      // Books
      'book', 'books', 'novel', 'fiction', 'non-fiction', 'biography', 'autobiography',
      'memoir', 'poetry', 'drama', 'thriller', 'mystery', 'romance', 'fantasy',
      'science fiction', 'horror', 'adventure', 'history', 'philosophy',
      // Education
      'education', 'learning', 'study', 'course', 'textbook', 'manual', 'guide',
      'tutorial', 'lesson', 'class', 'school', 'university', 'college', 'academic',
      // Digital
      'ebook', 'audiobook', 'kindle', 'epub', 'pdf', 'digital book',
      // Italian
      'libro', 'libri', 'romanzo', 'narrativa', 'biografia', 'autobiografia',
      'poesia', 'dramma', 'thriller', 'mistero', 'romantico', 'fantasia',
      'fantascienza', 'horror', 'avventura', 'storia', 'filosofia',
      'educazione', 'apprendimento', 'studio', 'corso', 'manuale', 'guida',
      // Spanish
      'libro', 'libros', 'novela', 'ficción', 'biografía', 'autobiografía',
      'poesía', 'drama', 'thriller', 'misterio', 'romance', 'fantasía',
      'ciencia ficción', 'terror', 'aventura', 'historia', 'filosofía',
      'educación', 'aprendizaje', 'estudio', 'curso', 'manual', 'guía',
      // French
      'livre', 'livres', 'roman', 'fiction', 'biographie', 'autobiographie',
      'poésie', 'drame', 'thriller', 'mystère', 'romance', 'fantaisie',
      'science-fiction', 'horreur', 'aventure', 'histoire', 'philosophie',
      'éducation', 'apprentissage', 'étude', 'cours', 'manuel', 'guide',
      // German
      'buch', 'bücher', 'roman', 'fiktion', 'biografie', 'autobiografie',
      'poesie', 'drama', 'thriller', 'mysterium', 'romantik', 'fantasie',
      'science fiction', 'horror', 'abenteuer', 'geschichte', 'philosophie',
      'bildung', 'lernen', 'studium', 'kurs', 'handbuch', 'anleitung',
      // Portuguese
      'livro', 'livros', 'romance', 'ficção', 'biografia', 'autobiografia',
      'poesia', 'drama', 'thriller', 'mistério', 'romance', 'fantasia',
      'ficção científica', 'terror', 'aventura', 'história', 'filosofia',
      'educação', 'aprendizado', 'estudo', 'curso', 'manual', 'guia'
    ],
    domains: [
      'amazon.com', 'barnes-noble.com', 'bookdepository.com', 'waterstones.com',
      'goodreads.com', 'audible.com', 'scribd.com', 'kobo.com', 'apple.com',
      'google.com', 'coursera.org', 'udemy.com', 'edx.org', 'skillshare.com'
    ]
  },

  travel: {
    name: 'Travel & Adventure',
    keywords: [
      // Travel
      'travel', 'trip', 'vacation', 'holiday', 'journey', 'adventure', 'explore',
      'tourism', 'tourist', 'destination', 'flight', 'hotel', 'accommodation',
      'booking', 'reservation', 'passport', 'visa', 'luggage', 'suitcase',
      'backpack', 'camera', 'guide', 'map', 'itinerary',
      // Activities
      'hiking', 'trekking', 'camping', 'beach', 'mountain', 'city', 'culture',
      'museum', 'restaurant', 'food', 'local', 'experience', 'tour',
      // Italian
      'viaggio', 'vacanza', 'avventura', 'esplorare', 'turismo', 'destinazione',
      'volo', 'hotel', 'alloggio', 'prenotazione', 'passaporto', 'visto',
      'bagaglio', 'valigia', 'zaino', 'fotocamera', 'guida', 'mappa',
      'escursionismo', 'campeggio', 'spiaggia', 'montagna', 'città', 'cultura',
      // Spanish
      'viaje', 'vacaciones', 'aventura', 'explorar', 'turismo', 'destino',
      'vuelo', 'hotel', 'alojamiento', 'reserva', 'pasaporte', 'visa',
      'equipaje', 'maleta', 'mochila', 'cámara', 'guía', 'mapa',
      'senderismo', 'acampada', 'playa', 'montaña', 'ciudad', 'cultura',
      // French
      'voyage', 'vacances', 'aventure', 'explorer', 'tourisme', 'destination',
      'vol', 'hôtel', 'hébergement', 'réservation', 'passeport', 'visa',
      'bagages', 'valise', 'sac à dos', 'appareil photo', 'guide', 'carte',
      'randonnée', 'camping', 'plage', 'montagne', 'ville', 'culture',
      // German
      'reise', 'urlaub', 'abenteuer', 'erkunden', 'tourismus', 'ziel',
      'flug', 'hotel', 'unterkunft', 'buchung', 'reisepass', 'visum',
      'gepäck', 'koffer', 'rucksack', 'kamera', 'führer', 'karte',
      'wandern', 'camping', 'strand', 'berg', 'stadt', 'kultur',
      // Portuguese
      'viagem', 'férias', 'aventura', 'explorar', 'turismo', 'destino',
      'voo', 'hotel', 'acomodação', 'reserva', 'passaporte', 'visto',
      'bagagem', 'mala', 'mochila', 'câmera', 'guia', 'mapa',
      'caminhada', 'acampamento', 'praia', 'montanha', 'cidade', 'cultura'
    ],
    domains: [
      'booking.com', 'expedia.com', 'airbnb.com', 'tripadvisor.com', 'kayak.com',
      'skyscanner.com', 'hotels.com', 'agoda.com', 'priceline.com', 'trivago.com',
      'lonelyplanet.com', 'viator.com', 'getyourguide.com', 'klook.com'
    ]
  },

  food: {
    name: 'Food & Dining',
    keywords: [
      // Food types
      'food', 'restaurant', 'dining', 'cuisine', 'recipe', 'cooking', 'kitchen',
      'chef', 'meal', 'breakfast', 'lunch', 'dinner', 'snack', 'dessert',
      'appetizer', 'main course', 'beverage', 'drink', 'coffee', 'tea', 'wine',
      'beer', 'cocktail', 'juice', 'water',
      // Cooking
      'ingredients', 'spices', 'herbs', 'sauce', 'seasoning', 'marinade',
      'baking', 'grilling', 'frying', 'roasting', 'steaming', 'boiling',
      // Equipment
      'cookware', 'utensils', 'knife', 'pan', 'pot', 'oven', 'stove', 'blender',
      'mixer', 'processor', 'cutting board', 'measuring cup',
      // Italian
      'cibo', 'ristorante', 'cucina', 'ricetta', 'cucinare', 'chef', 'pasto',
      'colazione', 'pranzo', 'cena', 'spuntino', 'dolce', 'antipasto',
      'bevanda', 'caffè', 'tè', 'vino', 'birra', 'cocktail', 'succo',
      'ingredienti', 'spezie', 'erbe', 'salsa', 'condimento', 'marinata',
      // Spanish
      'comida', 'restaurante', 'cocina', 'receta', 'cocinar', 'chef', 'comida',
      'desayuno', 'almuerzo', 'cena', 'merienda', 'postre', 'aperitivo',
      'bebida', 'café', 'té', 'vino', 'cerveza', 'cóctel', 'jugo',
      'ingredientes', 'especias', 'hierbas', 'salsa', 'condimento', 'adobo',
      // French
      'nourriture', 'restaurant', 'cuisine', 'recette', 'cuisiner', 'chef', 'repas',
      'petit-déjeuner', 'déjeuner', 'dîner', 'collation', 'dessert', 'apéritif',
      'boisson', 'café', 'thé', 'vin', 'bière', 'cocktail', 'jus',
      'ingrédients', 'épices', 'herbes', 'sauce', 'assaisonnement', 'marinade',
      // German
      'essen', 'restaurant', 'küche', 'rezept', 'kochen', 'koch', 'mahlzeit',
      'frühstück', 'mittagessen', 'abendessen', 'snack', 'nachtisch', 'vorspeise',
      'getränk', 'kaffee', 'tee', 'wein', 'bier', 'cocktail', 'saft',
      'zutaten', 'gewürze', 'kräuter', 'soße', 'würze', 'marinade',
      // Portuguese
      'comida', 'restaurante', 'cozinha', 'receita', 'cozinhar', 'chef', 'refeição',
      'café da manhã', 'almoço', 'jantar', 'lanche', 'sobremesa', 'aperitivo',
      'bebida', 'café', 'chá', 'vinho', 'cerveja', 'coquetel', 'suco',
      'ingredientes', 'temperos', 'ervas', 'molho', 'tempero', 'marinada'
    ],
    domains: [
      'uber.com', 'doordash.com', 'grubhub.com', 'seamless.com', 'deliveroo.com',
      'foodpanda.com', 'zomato.com', 'yelp.com', 'opentable.com', 'resy.com',
      'allrecipes.com', 'foodnetwork.com', 'epicurious.com', 'bonappetit.com'
    ]
  }
};

/**
 * Categorizes a product based on its title, URL, and context
 * @param {Object} product - Product object with title, url, etc.
 * @returns {Object} - Category analysis with scores
 */
export function categorizeProduct(product) {
  if (!product) return { category: 'other', confidence: 0, scores: {} };

  const title = (product.title || '').toLowerCase();
  const url = (product.url || '').toLowerCase();
  const scores = {};

  // Calculate scores for each category
  Object.keys(CATEGORIES).forEach(categoryKey => {
    const category = CATEGORIES[categoryKey];
    let score = 0;

    // Check keywords in title
    category.keywords.forEach(keyword => {
      if (title.includes(keyword.toLowerCase())) {
        // Exact word match gets higher score
        const exactMatch = new RegExp(`\\b${keyword.toLowerCase()}\\b`).test(title);
        score += exactMatch ? 2 : 1;
      }
    });

    // Check domain match
    category.domains.forEach(domain => {
      if (url.includes(domain.toLowerCase())) {
        score += 5; // Domain match gets high weight
      }
    });

    scores[categoryKey] = score;
  });

  // Find highest scoring category
  const maxScore = Math.max(...Object.values(scores));
  const topCategory = Object.keys(scores).find(key => scores[key] === maxScore);

  // Calculate confidence based on score distribution
  const totalScore = Object.values(scores).reduce((sum, score) => sum + score, 0);
  const confidence = totalScore > 0 ? (maxScore / totalScore) : 0;

  return {
    category: maxScore > 0 ? topCategory : 'other',
    confidence: Math.min(confidence, 1),
    scores,
    details: {
      title,
      analyzedUrl: url,
      maxScore,
      totalScore
    }
  };
}

/**
 * Analyzes multiple products and returns category distribution
 * @param {Array} products - Array of product objects
 * @returns {Object} - Category distribution analysis
 */
export function analyzeProductCategories(products = []) {
  if (!Array.isArray(products) || products.length === 0) {
    return { distribution: {}, totalProducts: 0, topCategories: [] };
  }

  const distribution = {};
  const confidenceThreshold = 0.3; // Minimum confidence to count the category

  products.forEach(product => {
    const analysis = categorizeProduct(product);
    
    if (analysis.confidence >= confidenceThreshold) {
      const category = analysis.category;
      if (!distribution[category]) {
        distribution[category] = {
          count: 0,
          percentage: 0,
          avgConfidence: 0,
          products: []
        };
      }
      
      distribution[category].count++;
      distribution[category].products.push({
        title: product.title,
        confidence: analysis.confidence
      });
    }
  });

  // Calculate percentages and average confidence
  const totalCategorized = Object.values(distribution).reduce((sum, cat) => sum + cat.count, 0);
  
  Object.keys(distribution).forEach(category => {
    const cat = distribution[category];
    cat.percentage = totalCategorized > 0 ? (cat.count / totalCategorized) : 0;
    cat.avgConfidence = cat.products.reduce((sum, p) => sum + p.confidence, 0) / cat.count;
  });

  // Get top categories sorted by count
  const topCategories = Object.entries(distribution)
    .sort(([,a], [,b]) => b.count - a.count)
    .slice(0, 5)
    .map(([category, data]) => ({
      category,
      name: CATEGORIES[category]?.name || category,
      ...data
    }));

  return {
    distribution,
    totalProducts: products.length,
    totalCategorized,
    topCategories,
    categorizationRate: products.length > 0 ? (totalCategorized / products.length) : 0
  };
}

/**
 * Gets category information
 * @param {string} categoryKey - Category key
 * @returns {Object} - Category information
 */
export function getCategoryInfo(categoryKey) {
  return CATEGORIES[categoryKey] || { name: 'Other', keywords: [], domains: [] };
}

/**
 * Gets all available categories
 * @returns {Object} - All categories with their information
 */
export function getAllCategories() {
  return Object.keys(CATEGORIES).map(key => ({
    key,
    ...CATEGORIES[key]
  }));
}